import React, { useMemo, useRef, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { useUpcomingToursWithStatus, useContractList, useUser } from '../../../hooks'
import { formatDateDot } from '../../../utils'
import styles from './HomeScheduleCalendar.module.scss'

/**
 * 홈 화면용 일정 캘린더
 * - 얇은 이벤트 바로 대회 표시 (계약: 녹색, 미계약: 회색)
 * - 이벤트 클릭 시 대회명 + 기간 툴팁 표시
 */
export const HomeScheduleCalendar = () => {
	const calendarRef = useRef(null)
	const now = new Date()
	const [calendarDate, setCalendarDate] = useState(now.toISOString().split('T')[0])
	const [currentTitle, setCurrentTitle] = useState('')
	// 클릭한 대회 정보 (툴팁용)
	const [tooltip, setTooltip] = useState(null) // { name, period, x, y }
	// +N more 클릭 시 — FullCalendar 기본 popover는 DOM/스크롤 구조에 따라 getBoundingClientRect 오류가 나므로 비활성화하고 직접 표시
	const [moreList, setMoreList] = useState(null) // { items: [{ name, period, isContracted }], x, y }

	const handlePrev = () => {
		const api = calendarRef.current?.getApi()
		if (api) api.prev()
	}
	const handleNext = () => {
		const api = calendarRef.current?.getApi()
		if (api) api.next()
	}

	// 유저 정보 (프로: tour_type으로 성별 판별)
	const { data: user } = useUser()

	// 다가오는 대회 (최대 50개)
	const { data: upcomingTours = [] } = useUpcomingToursWithStatus(50)
	const toursRaw = Array.isArray(upcomingTours) ? upcomingTours : []

	// 프로는 자기 성별에 맞는 대회만 표시 (KPGA/KLPGA), 캐디는 전체
	const toursList = useMemo(() => {
		if (user?.userType !== 'tourPro') return toursRaw // 캐디: 전체 표시
		const proTourType = user?.tourPro?.tour_type // 'KPGA' 또는 'KLPGA'
		if (!proTourType) return toursRaw
		return toursRaw.filter((t) => t.type === proTourType)
	}, [toursRaw, user])

	// 계약 목록에서 체결 완료된 대회 ID만 추출 (ACCEPTED만)
	const { data: contracts = [] } = useContractList()
	const contractedTourIds = useMemo(() => {
		if (!Array.isArray(contracts)) return new Set()
		const ids = new Set()
		contracts.forEach((c) => {
			if (c?.contract_status === 'ACCEPTED') {
				;(c.contractTours || []).forEach((ct) => {
					if (ct.tour?.id) ids.add(ct.tour.id)
				})
			}
		})
		return ids
	}, [contracts])

	// 계약에만 있고 upcoming에 없는 대회 추출 (프로는 성별 필터 적용)
	const contractOnlyTours = useMemo(() => {
		if (!Array.isArray(contracts)) return []
		const upcomingIds = new Set(toursList.map((t) => t.id))
		const extra = []
		const seen = new Set()
		const proTourType = user?.userType === 'tourPro' ? user?.tourPro?.tour_type : null
		contracts.forEach((c) => {
			// 취소/파기된 계약은 제외
			if (c?.contract_status === 'CANCELED' || c?.contract_status === 'TERMINATED') return
			;(c.contractTours || []).forEach((ct) => {
				if (ct.tour?.id && !upcomingIds.has(ct.tour.id) && !seen.has(ct.tour.id)) {
					// 프로는 자기 성별 대회만
					if (proTourType && ct.tour.type && ct.tour.type !== proTourType) return
					extra.push(ct.tour)
					seen.add(ct.tour.id)
				}
			})
		})
		return extra
	}, [contracts, toursList, user])

	// FullCalendar 이벤트 배열 (얇은 바)
	const calendarEvents = useMemo(() => {
		const events = []
		const seen = new Set()

		// 1) upcoming 대회
		toursList.forEach((tour) => {
			if (seen.has(tour.id)) return
			seen.add(tour.id)
			const end = new Date(tour.ended_at)
			end.setDate(end.getDate() + 1) // FullCalendar exclusive end
			const isContracted = tour.label === '계약 완료' || contractedTourIds.has(tour.id)

			events.push({
				id: tour.id,
				title: '', // 텍스트 숨김 (바만 표시)
				start: tour.started_at,
				end: end.toISOString().split('T')[0],
				allDay: true,
				backgroundColor: isContracted ? '#085e4b' : '#d6d6d6',
				borderColor: isContracted ? '#085e4b' : '#d6d6d6',
				extendedProps: {
					name: tour.name,
					period: `${formatDateDot(tour.started_at)} ~ ${formatDateDot(tour.ended_at)}`,
					isContracted,
				},
			})
		})

		// 2) 계약에만 있는 대회 (ACCEPTED만 녹색, 나머지는 회색)
		contractOnlyTours.forEach((tour) => {
			if (seen.has(tour.id)) return
			seen.add(tour.id)
			const end = new Date(tour.ended_at)
			end.setDate(end.getDate() + 1)
			const isContracted = contractedTourIds.has(tour.id)

			events.push({
				id: tour.id,
				title: '',
				start: tour.started_at,
				end: end.toISOString().split('T')[0],
				allDay: true,
				backgroundColor: isContracted ? '#085e4b' : '#d6d6d6',
				borderColor: isContracted ? '#085e4b' : '#d6d6d6',
				extendedProps: {
					name: tour.name,
					period: `${formatDateDot(tour.started_at)} ~ ${formatDateDot(tour.ended_at)}`,
					isContracted,
				},
			})
		})

		return events
	}, [toursList, contractOnlyTours, contractedTourIds])

	const handleEventClick = (info) => {
		if (!info?.el) return
		setMoreList(null)
		const { name, period, isContracted } = info.event.extendedProps
		const rect = info.el.getBoundingClientRect()
		// 달력 카드 기준 상대 좌표 계산
		const cardEl = info.el.closest(`.${styles.calendarCard}`)
		const cardRect = cardEl?.getBoundingClientRect() || { left: 0, top: 0 }

		setTooltip({
			name,
			period,
			isContracted,
			x: rect.left - cardRect.left + rect.width / 2,
			y: rect.bottom - cardRect.top + 4,
		})
	}

	const handleMoreLinkClick = (arg) => {
		setTooltip(null)
		const items = arg.hiddenSegs.map((seg) => {
			const e = seg.event
			const ep = e.extendedProps || {}
			return {
				name: ep.name || e.title || '일정',
				period: ep.period || '',
				isContracted: !!ep.isContracted,
			}
		})
		const target = arg.jsEvent?.target
		const anchorEl = target instanceof Element ? target : target?.parentElement
		const anchor = anchorEl?.closest?.('.fc-daygrid-more-link') ?? null
		let x = 0
		let y = 0
		if (anchor) {
			const rect = anchor.getBoundingClientRect()
			const cardEl = anchor.closest(`.${styles.calendarCard}`)
			const cardRect = cardEl?.getBoundingClientRect() || { left: 0, top: 0 }
			x = rect.left - cardRect.left + rect.width / 2
			y = rect.bottom - cardRect.top + 4
		} else if (arg.jsEvent && 'clientX' in arg.jsEvent) {
			const cardEl = document.querySelector(`.${styles.calendarCard}`)
			const cardRect = cardEl?.getBoundingClientRect()
			if (cardRect) {
				x = arg.jsEvent.clientX - cardRect.left
				y = arg.jsEvent.clientY - cardRect.top
			}
		}
		setMoreList({ items, x, y })
		return 'none'
	}

	return (
		<div className={styles.section}>
			<div
				className={styles.calendarCard}
				onClick={(e) => {
					// 이벤트 바·추가 일정 패널·+more 링크 외부 클릭 시 닫기 (+more 클릭은 moreLinkClick 직후 버블로 지우지 않도록 제외)
					if (
						!e.target.closest('.fc-event') &&
						!e.target.closest(`.${styles.moreListPopover}`) &&
						!e.target.closest('.fc-daygrid-more-link')
					) {
						setTooltip(null)
						setMoreList(null)
					}
				}}>
				{/* 커스텀 헤더: 타이틀 + 월 네비게이션 한 줄 */}
				<div className={styles.calendarHeader}>
					<span className={styles.boxLabel}>일정 캘린더</span>
					<div className={styles.monthNav}>
						<button
							type="button"
							className={styles.navBtn}
							onClick={handlePrev}
							aria-label="이전 달">
							<ChevronLeftIcon className={styles.navIcon} />
						</button>
						<span className={styles.monthTitle}>{currentTitle}</span>
						<button
							type="button"
							className={styles.navBtn}
							onClick={handleNext}
							aria-label="다음 달">
							<ChevronRightIcon className={styles.navIcon} />
						</button>
					</div>
				</div>

				<FullCalendar
					ref={calendarRef}
					plugins={[dayGridPlugin, interactionPlugin]}
					locale="ko"
					firstDay={1}
					initialDate={calendarDate}
					initialView="dayGridMonth"
					headerToolbar={false}
					height="auto"
					contentHeight="auto"
					dayMaxEventRows={4}
					events={calendarEvents}
					eventClick={handleEventClick}
					moreLinkClick={handleMoreLinkClick}
					datesSet={(dateInfo) => {
						setCalendarDate(dateInfo.start.toISOString().split('T')[0])
						setTooltip(null)
						setMoreList(null)
						setCurrentTitle(dateInfo.view.title)
					}}
				/>

				{/* +more 로 열리는 추가 일정 목록 (기본 popover 미사용) */}
				{moreList && moreList.items?.length > 0 && (
					<div
						className={styles.moreListPopover}
						style={{ left: moreList.x, top: moreList.y }}
						role="dialog"
						aria-label="같은 날 추가 일정">
						<div className={styles.moreListTitle}>이 날 일정</div>
						<ul className={styles.moreListUl}>
							{moreList.items.map((row, idx) => (
								<li key={idx} className={styles.moreListLi}>
									<div className={styles.moreListRowHeader}>
										<span
											className={`${styles.tooltipDot} ${
												row.isContracted
													? styles.contractedDot
													: styles.uncontractedDot
											}`}
										/>
										<span className={styles.tooltipStatus}>
											{row.isContracted ? '계약 완료' : '미계약'}
										</span>
									</div>
									<div className={styles.tooltipName}>{row.name}</div>
									{row.period ? (
										<div className={styles.tooltipPeriod}>{row.period}</div>
									) : null}
								</li>
							))}
						</ul>
					</div>
				)}

				{/* 툴팁 */}
				{tooltip && (
					<div className={styles.tooltip} style={{ left: tooltip.x, top: tooltip.y }}>
						<div className={styles.tooltipHeader}>
							<span
								className={`${styles.tooltipDot} ${
									tooltip.isContracted
										? styles.contractedDot
										: styles.uncontractedDot
								}`}
							/>
							<span className={styles.tooltipStatus}>
								{tooltip.isContracted ? '계약 완료' : '미계약'}
							</span>
						</div>
						<div className={styles.tooltipName}>{tooltip.name}</div>
						<div className={styles.tooltipPeriod}>{tooltip.period}</div>
					</div>
				)}

				{/* 범례 */}
				<div className={styles.legend}>
					<div className={styles.legendItem}>
						<span className={`${styles.legendDot} ${styles.contractedDot}`} />
						<span className={styles.legendLabel}>계약된 대회</span>
					</div>
					<div className={styles.legendItem}>
						<span className={`${styles.legendDot} ${styles.uncontractedDot}`} />
						<span className={styles.legendLabel}>미계약 대회</span>
					</div>
				</div>
			</div>
		</div>
	)
}
